<template>
  <div>
    <br />
    
    <!-- Section 1: Spreadsheet ID -->
    <v-sheet class="pad-5 mb-5">
      <v-card elevation="2">
        <v-card-title class="section-title">
          <h3>معرف جدول البيانات (Spreadsheet ID)</h3>
        </v-card-title>
        <v-card-text>
          <v-form ref="spreadsheetIdForm" @submit.prevent="saveSpreadsheetId">
            <v-row>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="spreadsheetId"
                  label="معرف جدول البيانات"
                  outlined
                  dense
                  :loading="loadingSpreadsheetId"
                  hint="أدخل معرف جدول البيانات من Google Sheets"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-center">
                <v-btn
                  color="primary"
                  @click="saveSpreadsheetId"
                  :loading="savingSpreadsheetId"
                  :disabled="!spreadsheetId"
                  style="height: 40px; width: 100%;"
                >
                  حفظ
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </v-sheet>

    <!-- Section 2: Import Config -->
    <v-sheet class="pad-5 mb-5">
      <v-card elevation="2">
        <v-card-title class="section-title">
          <h3>إعدادات الاستيراد (Import Config)</h3>
        </v-card-title>
        <v-card-text>
          <v-form ref="importConfigForm" @submit.prevent="saveImportConfig">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="importConfig.sheet_name"
                  label="اسم الورقة (Sheet Name)"
                  outlined
                  dense
                  required
                  :rules="[v => !!v || 'اسم الورقة مطلوب']"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="importConfig.source_id"
                  :items="sources"
                  label="مصدر المعاملة (Source)"
                  item-text="value"
                  item-value="id"
                  outlined
                  dense
                  required
                  :rules="[v => !!v || 'مصدر المعاملة مطلوب']"
                  :loading="loadingSources"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12">
                <v-divider class="my-4"></v-divider>
                <h4 class="mb-3">أعمدة البيانات (Columns)</h4>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="importConfig.columns.phone"
                  label="عمود الجوال (Phone Column)"
                  outlined
                  dense
                  required
                  :rules="columnRules"
                  hint="مثال: A, N, AA"
                  persistent-hint
                  @input="uppercaseColumn('phone')"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="importConfig.columns.first_name"
                  label="عمود الاسم الأول (First Name Column)"
                  outlined
                  dense
                  required
                  :rules="columnRules"
                  hint="مثال: B, O, AB"
                  persistent-hint
                  @input="uppercaseColumn('first_name')"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="importConfig.columns.last_name"
                  label="عمود اسم العائلة (Last Name Column)"
                  outlined
                  dense
                  required
                  :rules="columnRules"
                  hint="مثال: C, P, AC"
                  persistent-hint
                  @input="uppercaseColumn('last_name')"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="importConfig.columns.created_at"
                  label="عمود تاريخ الإنشاء (Created At Column)"
                  outlined
                  dense
                  required
                  :rules="columnRules"
                  hint="مثال: D, Q, AD"
                  persistent-hint
                  @input="uppercaseColumn('created_at')"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="importConfig.columns.property_type"
                  label="عمود نوع العقار (Property Type Column)"
                  outlined
                  dense
                  required
                  :rules="columnRules"
                  hint="مثال: E, R, AE"
                  persistent-hint
                  @input="uppercaseColumn('property_type')"
                ></v-text-field>
              </v-col>

              <v-col cols="12" class="d-flex justify-end">
                <v-btn
                  color="primary"
                  @click="saveImportConfig"
                  :loading="savingImportConfig"
                  type="submit"
                >
                  حفظ الإعدادات
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </v-sheet>

    <!-- Section 3: Run Import -->
    <v-sheet class="pad-5 mb-5">
      <v-card elevation="2">
        <v-card-title class="section-title">
          <h3>تشغيل الاستيراد (Run Import)</h3>
        </v-card-title>
        <v-card-text>
          <v-form ref="runImportForm" @submit.prevent="runImport">
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="importLimit"
                  label="عدد السجلات (Limit)"
                  type="number"
                  outlined
                  dense
                  required
                  :rules="[v => !!v && v > 0 || 'يجب إدخال عدد أكبر من صفر']"
                  hint="عدد السجلات المراد استيرادها"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="8" class="d-flex align-center">
                <v-btn
                  color="success"
                  large
                  @click="runImport"
                  :loading="runningImport"
                  :disabled="!importLimit || importLimit <= 0"
                  style="height: 40px;"
                >
                  تشغيل الاستيراد
                </v-btn>
              </v-col>
            </v-row>
          </v-form>

          <!-- Import Results -->
          <v-row v-if="importResults" class="mt-4">
            <v-col cols="12">
              <v-divider class="mb-4"></v-divider>
              <h4 class="mb-3">نتائج الاستيراد</h4>
              <v-row>
                <v-col cols="12" md="3">
                  <v-card color="success" dark>
                    <v-card-text>
                      <div class="text-h6">جديد</div>
                      <div class="text-h4">{{ importResults.new || 0 }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card color="warning" dark>
                    <v-card-text>
                      <div class="text-h6">مكرر</div>
                      <div class="text-h4">{{ importResults.duplicated || 0 }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card color="info" dark>
                    <v-card-text>
                      <div class="text-h6">تمت المعالجة</div>
                      <div class="text-h4">{{ importResults.processed || 0 }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3" v-if="importResults.start_row || importResults.last_processed_row">
                  <v-card color="primary" dark>
                    <v-card-text>
                      <div class="text-h6">من صف</div>
                      <div class="text-h4">{{ importResults.start_row || '-' }}</div>
                      <div class="text-subtitle-2 mt-2">إلى صف</div>
                      <div class="text-h6">{{ importResults.last_processed_row || '-' }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-sheet>
  </div>
</template>

<script>
import { axiosInstance } from "@/axios/config";
import { base } from "@/mixins/base";

export default {
  name: "GoogleSheetImport",
  mixins: [base],
  data() {
    return {
      spreadsheetId: "",
      loadingSpreadsheetId: false,
      savingSpreadsheetId: false,
      
      importConfig: {
        sheet_name: "Sheet1",
        columns: {
          phone: "",
          first_name: "",
          last_name: "",
          created_at: "",
          property_type: ""
        },
        source_id: null
      },
      loadingImportConfig: false,
      savingImportConfig: false,
      loadingSources: false,
      
      importLimit: 100,
      runningImport: false,
      importResults: null,
      
      columnRules: [
        v => !!v || 'حقل العمود مطلوب',
        v => /^[A-Za-z]{1,3}$/.test(v) || 'يجب أن يكون حرف أو حرفين أو ثلاثة أحرف (A-Z)'
      ]
    };
  },
  computed: {
    sources() {
      return this.$store.state.work_sources || [];
    }
  },
  mounted() {
    this.loadSpreadsheetId();
    this.loadImportConfig();
    this.loadSources();
  },
  methods: {
    // Section 1: Spreadsheet ID
    async loadSpreadsheetId() {
      this.loadingSpreadsheetId = true;
      try {
        const response = await axiosInstance.get(
          "/employee/admin/google-sheet/spreadsheet-id",
          { headers: this.request_headers() }
        );
        if (response.data && response.data.payload) {
          this.spreadsheetId = response.data.payload.spreadsheet_id || "";
        }
      } catch (error) {
        console.error("Error loading spreadsheet ID:", error);
        if (error.response && error.response.status !== 404) {
          this.showPopupError("error", error.response?.data?.message || "حدث خطأ أثناء تحميل معرف جدول البيانات");
        }
      } finally {
        this.loadingSpreadsheetId = false;
      }
    },
    
    async saveSpreadsheetId() {
      if (!this.spreadsheetId) {
        this.showPopupError("error", "يرجى إدخال معرف جدول البيانات");
        return;
      }
      
      this.savingSpreadsheetId = true;
      try {
        const response = await axiosInstance.put(
          "/employee/admin/google-sheet/spreadsheet-id",
          { spreadsheet_id: this.spreadsheetId },
          { headers: this.request_headers() }
        );
        this.showPopup("success", response.data?.message || "تم حفظ معرف جدول البيانات بنجاح");
      } catch (error) {
        console.error("Error saving spreadsheet ID:", error);
        this.showPopupError("error", error.response?.data?.message || "حدث خطأ أثناء حفظ معرف جدول البيانات");
      } finally {
        this.savingSpreadsheetId = false;
      }
    },

    // Section 2: Import Config
    async loadImportConfig() {
      this.loadingImportConfig = true;
      try {
        const response = await axiosInstance.get(
          "/employee/admin/google-sheet/import-config",
          { headers: this.request_headers() }
        );
        if (response.data && response.data.payload && response.data.payload.config) {
          const config = response.data.payload.config;
          this.importConfig = {
            sheet_name: config.sheet_name || "Sheet1",
            columns: {
              phone: config.columns?.phone || "",
              first_name: config.columns?.first_name || "",
              last_name: config.columns?.last_name || "",
              created_at: config.columns?.created_at || "",
              property_type: config.columns?.property_type || ""
            },
            source_id: config.source_id || null
          };
        }
      } catch (error) {
        console.error("Error loading import config:", error);
        if (error.response && error.response.status !== 404) {
          this.showPopupError("error", error.response?.data?.message || "حدث خطأ أثناء تحميل إعدادات الاستيراد");
        }
      } finally {
        this.loadingImportConfig = false;
      }
    },
    
    uppercaseColumn(field) {
      if (this.importConfig.columns[field]) {
        this.importConfig.columns[field] = this.importConfig.columns[field].toUpperCase();
      }
    },
    
    async saveImportConfig() {
      if (this.$refs.importConfigForm && !this.$refs.importConfigForm.validate()) {
        this.showPopupError("error", "يرجى التحقق من صحة البيانات المدخلة");
        return;
      }
      
      this.savingImportConfig = true;
      try {
        // Validate column letters
        const columns = this.importConfig.columns;
        for (const [key, value] of Object.entries(columns)) {
          if (!value || !/^[A-Za-z]{1,3}$/.test(value)) {
            this.showPopupError("error", `حقل العمود ${key} غير صحيح. يجب أن يكون حرف أو حرفين أو ثلاثة أحرف (A-Z)`);
            this.savingImportConfig = false;
            return;
          }
        }
        
        // Get source name from selected source
        const selectedSource = this.sources.find(s => s.id === this.importConfig.source_id);
        const source_name = selectedSource ? selectedSource.value : "";
        
        // Uppercase all column letters
        const configToSave = {
          ...this.importConfig,
          source_name: source_name,
          columns: {
            phone: this.importConfig.columns.phone.toUpperCase(),
            first_name: this.importConfig.columns.first_name.toUpperCase(),
            last_name: this.importConfig.columns.last_name.toUpperCase(),
            created_at: this.importConfig.columns.created_at.toUpperCase(),
            property_type: this.importConfig.columns.property_type.toUpperCase()
          }
        };
        
        const response = await axiosInstance.put(
          "/employee/admin/google-sheet/import-config",
          configToSave,
          { headers: this.request_headers() }
        );
        this.showPopup("success", response.data?.message || "تم حفظ إعدادات الاستيراد بنجاح");
      } catch (error) {
        console.error("Error saving import config:", error);
        this.showPopupError("error", error.response?.data?.message || "حدث خطأ أثناء حفظ إعدادات الاستيراد");
      } finally {
        this.savingImportConfig = false;
      }
    },

    // Section 3: Run Import
    async runImport() {
      if (!this.importLimit || this.importLimit <= 0) {
        this.showPopupError("error", "يرجى إدخال عدد صحيح أكبر من صفر");
        return;
      }
      
      this.runningImport = true;
      this.importResults = null;
      try {
        const response = await axiosInstance.post(
          "/employee/admin/google-sheet/import-seyoula",
          { limit: this.importLimit },
          { headers: this.request_headers() }
        );
        
        if (response.data && response.data.payload) {
          this.importResults = {
            new: response.data.payload.new || 0,
            duplicated: response.data.payload.duplicated || 0,
            processed: response.data.payload.processed || 0,
            start_row: response.data.payload.start_row,
            last_processed_row: response.data.payload.last_processed_row
          };
        }
        
        this.showPopup("success", response.data?.message || "تم تشغيل الاستيراد بنجاح");
      } catch (error) {
        console.error("Error running import:", error);
        this.showPopupError("error", error.response?.data?.message || "حدث خطأ أثناء تشغيل الاستيراد");
      } finally {
        this.runningImport = false;
      }
    },
    
    async loadSources() {
      if (this.$store.state.work_sources && this.$store.state.work_sources.length > 0) {
        return;
      }
      
      this.loadingSources = true;
      try {
        const response = await axiosInstance.get(
          "/agent/Request/Sources",
          { headers: this.request_headers() }
        );
        if (response.data && response.data.payload) {
          this.$store.state.work_sources = response.data.payload;
        }
      } catch (error) {
        console.error("Error loading sources:", error);
        // Don't show error popup, just log it - sources might already be loaded
      } finally {
        this.loadingSources = false;
      }
    }
  }
};
</script>

<style scoped>
.pad-5 {
  padding: 0px 20px;
}

.section-title {
  background-color: #5c315d;
  color: white;
}

.section-title h3 {
  color: white;
  margin: 0;
  font-size: 1.25rem;
}

.v-card {
  border-radius: 8px;
}

.v-text-field {
  font-size: 14px;
}

.v-btn {
  text-transform: none;
}
</style>
